import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';
import { BrandShare, useBrandShares, useSaveBrandShare } from '../../lib/api/campaigns';

/** Share one brand's plan across campaigns (Doreen, 2026-10-29): e.g. Wever
 *  & Ducré's Black Friday spots are spread over the Pre-Black Friday and
 *  Black Week campaigns. Each link covers a period and shows every
 *  placement tagged with the brand in it — read-only, no tasks or notes. */

const linkUrl = (token: string) => `${window.location.origin}/share/brand/${token}`;

const ShareLinkRow = ({ link, brandId }: { link: BrandShare; brandId: string }) => {
  const toast = useToast();
  const save = useSaveBrandShare(brandId);
  const [draft, setDraft] = useState(link);
  useEffect(() => setDraft(link), [link]);
  const changed = (['title', 'intro', 'date_from', 'date_to'] as const).some((k) => (draft[k] ?? '') !== (link[k] ?? ''));

  const update = async (patch: Partial<BrandShare>, done: string) => {
    try {
      await save.mutateAsync({ id: link.id, ...patch });
      toast.push(done);
    } catch {
      toast.push('Could not save the link');
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(linkUrl(link.token));
    toast.push('Link copied');
  };

  return (
    <div style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', background: 'var(--bg-subtle)', display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 650, fontSize: 13.5 }}>{link.title || 'Plan'}</div>
        <span
          style={{
            fontSize: 11, padding: '1px 8px', borderRadius: 999,
            background: link.enabled ? 'var(--seasonal)' : 'var(--border-strong)', color: link.enabled ? '#fff' : 'var(--ink-3)',
          }}
        >
          {link.enabled ? 'Shared' : 'Off'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {link.enabled ? (
            <>
              <Button variant="ghost" onClick={copy}><Icon name="link" size={13} /> Copy link</Button>
              <Button variant="ghost" onClick={() => update({ enabled: false }, 'Link is off — it no longer works')} style={{ color: 'var(--warn)' }}>
                Stop sharing
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => update({ enabled: true }, 'Link is on again')}>Turn back on</Button>
          )}
        </div>
      </div>
      {link.enabled && (
        <input className="input" readOnly value={linkUrl(link.token)} onFocus={(e) => e.target.select()} style={{ fontSize: 12 }} />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }} className="m-stack">
        <label style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          Title
          <input className="input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Black Friday 2026" />
        </label>
        <label style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          From
          <input className="input" type="date" value={draft.date_from ?? ''} onChange={(e) => setDraft({ ...draft, date_from: e.target.value || null })} />
        </label>
        <label style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          To
          <input className="input" type="date" value={draft.date_to ?? ''} onChange={(e) => setDraft({ ...draft, date_to: e.target.value || null })} />
        </label>
      </div>
      <label style={{ fontSize: 12, color: 'var(--ink-3)' }}>
        Text at the top of the page
        <textarea className="input" rows={2} value={draft.intro} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} placeholder="A short intro for this partner" />
      </label>
      {changed && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <Button variant="ghost" onClick={() => setDraft(link)}>Undo</Button>
          <Button
            variant="primary"
            disabled={save.isPending}
            onClick={() => update(
              { title: draft.title.trim(), intro: draft.intro.trim(), date_from: draft.date_from, date_to: draft.date_to },
              'Saved',
            )}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

const BrandShareModal = ({
  open,
  onClose,
  brandId,
  brandName,
}: {
  open: boolean;
  onClose: () => void;
  brandId: string;
  brandName: string;
}) => {
  const toast = useToast();
  const { data: links = [], isLoading } = useBrandShares(open ? brandId : '');
  const save = useSaveBrandShare(brandId);
  const [title, setTitle] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const create = async () => {
    try {
      const link = await save.mutateAsync({
        title: title.trim(), date_from: from || null, date_to: to || null,
        intro: `Here is everything we have planned for ${brandName} at LampTwist${title.trim() ? ` for ${title.trim()}` : ''}.`,
      });
      await navigator.clipboard.writeText(linkUrl(link.token));
      toast.push('Link created — copied');
      setTitle('');
      setFrom('');
      setTo('');
    } catch {
      toast.push('Could not create the link');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Share ${brandName}'s plan`} maxWidth={680}>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 0 }}>
        One page with every placement tagged with {brandName} in the period you pick, across all
        campaigns, grouped per campaign. Read-only: no tasks, no internal notes, no statuses. It
        stays up to date — new placements for {brandName} in the period show up by themselves.
      </p>

      <div style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', border: '1px dashed var(--border-strong)', display: 'grid', gap: 8, marginBottom: 12 }}>
        <div style={{ fontWeight: 650, fontSize: 13.5 }}>New link</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }} className="m-stack">
          <label style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            Title
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Black Friday 2026" />
          </label>
          <label style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            From
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            To
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <Button variant="primary" onClick={create} disabled={save.isPending || (!!from && !!to && to < from)}>
            Create & copy link
          </Button>
        </div>
        {!!from && !!to && to < from && (
          <div style={{ fontSize: 12, color: 'var(--warn)' }}>The end date is before the start date.</div>
        )}
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {isLoading && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>}
        {!isLoading && links.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No links yet for {brandName}.</div>
        )}
        {links.map((l) => <ShareLinkRow key={l.id} link={l} brandId={brandId} />)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
};

export default BrandShareModal;
